import { Injectable } from "@angular/core";
import { ConfigService } from "../config/config.service";
import { map, Observable, Subject } from "rxjs";
import { HttpClient, HttpRequest, HttpEventType } from "@angular/common/http";
import { DocumentActions, UploadType } from "../models/document.model";
import { UserModel } from "../models/user.model";

@Injectable({
  providedIn: "root",
})
export class FileService {
  constructor(
    private readonly config: ConfigService,
    private readonly httpClient: HttpClient,
  ) {}

  upload(
    files: Set<File>,
    uploadType: UploadType,
    attachmentTitle: string,
    attachmentDate: string,
    user: UserModel,
  ): {
    [key: string]: { progress: Observable<number>; response: Observable<any> };
  } {
    const url = this.getUrl(uploadType, DocumentActions.Add);

    const formData: FormData = new FormData();
    const file = this.getFirstFile(files);

    if (!file || !attachmentTitle || !attachmentDate || !user) return {};

    formData.append("file", file, file.name);
    formData.append("title", attachmentTitle);
    formData.append("name", attachmentTitle);
    formData.append("createdAt", attachmentDate);
    formData.append("createdBy", user.email);

    const req = new HttpRequest("POST", url, formData, {
      reportProgress: true,
    });

    const statusKey = file?.name || attachmentTitle || "file";
    return this.sendWithProgress(req, statusKey);
  }

  /**
   * Return first File from a Set<File> or null if none.
   */
  private getFirstFile(files: Set<File> | null | undefined): File | null {
    if (!files || files.size === 0) return null;
    // Convert iterator to first value quickly
    const it = files.values();
    const first = it.next();
    return first && !first.done ? first.value : null;
  }

  /**
   * Sends an HttpRequest and returns a status object with progress and response observables keyed by statusKey.
   */
  private sendWithProgress(
    req: HttpRequest<any>,
    statusKey: string,
  ): {
    [key: string]: { progress: Observable<number>; response: Observable<any> };
  } {
    const status: {
      [key: string]: {
        progress: Observable<number>;
        response: Observable<any>;
      };
    } = {};
    const progress = new Subject<number>();
    const response$ = new Subject<any>();

    this.httpClient.request(req).subscribe({
      next: (response) => {
        if (response.type === HttpEventType.UploadProgress) {
          // Protect against division by zero
          const percentDone = response.total
            ? Math.round((100 * response.loaded) / response.total)
            : 0;
          progress.next(percentDone);
        } else if (response.type === HttpEventType.Response) {
          // Treat any 2xx response as success
          if (response.status >= 200 && response.status < 300) {
            response$.next(response.body);
            response$.complete();
            progress.complete();
          }
        }
      },
      error: (error) => {
        progress.error(error);
        response$.error(error);
      },
    });

    status[statusKey] = {
      progress: progress.asObservable(),
      response: response$.asObservable(),
    };
    return status;
  }

  private getUrl(
    type: UploadType,
    action: string,
    id?: string,
    urlOrQueryParams?: string,
  ): string {
    const baseUrl = this.config.get("remoteContentApiUrl");

    // If full URL is passed, return it as-is
    if (urlOrQueryParams?.includes("https://")) {
      return urlOrQueryParams;
    }

    const routes = {
      [UploadType.Attachment]: {
        add: () => `${baseUrl}attachments`,
        get: () => `${baseUrl}attachments/${id}`,
        update: () => `${baseUrl}attachments/${id}`,
        delete: () => `${baseUrl}attachments/${id}`,
        downloadByAttachmentId: () => `${baseUrl}attachments/${id}/download`,
      },
    } as const;

    const typeRoutes = routes[type];
    const routeFn = typeRoutes ? (typeRoutes as any)[action] : undefined;

    if (!routeFn) {
      // Fallback: return baseUrl to avoid undefined
      return baseUrl;
    }

    const url = routeFn();
    return url.replace(/[?&]$/, "");
  }

  delete(fileId: string, documentType: UploadType) {
    const url = this.getUrl(documentType, "delete", fileId);
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    return this.httpClient.delete(url, { headers: headers }).pipe(
      map((res) => {
        return res;
      }),
    );
  }

  fetchFile(url: string, fileName: string): void {
    fetch(url, {
      headers: new Headers({
        Origin: location.origin,
      }),
      mode: "cors",
    })
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        this.forceDownload(blobUrl, fileName);
      })
      .catch((e) => console.error(e));
  }

  forceDownload(blob: string, filename: string): void {
    const a = document.createElement("a") as HTMLAnchorElement;
    a.download = filename;
    a.href = blob;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}
